import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ChevronDown, X, Check, Info, Scale, Ruler, Layers, Maximize, Lightbulb } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { categories, Product } from '../data/products';
// Flatten all products
const allProducts = categories.flatMap(cat => cat.products.map(p => ({
  ...p,
  categoryName: cat.name
})));
export function CompareMaterials() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Product-page comparisons always carry the current material plus two alternatives.
  // Keep that same three-material capacity at every screen size; the table can scroll.
  const maxSelections = 3;
  const [searchParams] = useSearchParams();
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>([]);
  useEffect(() => {
    const requestedSlugs = (searchParams.get('products')?.split(',') ?? [searchParams.get('add') ?? '']).filter(Boolean);
    const validSlugs = requestedSlugs.filter((slug, index) => allProducts.some(product => product.slug === slug) && requestedSlugs.indexOf(slug) === index).slice(0, maxSelections);
    if (validSlugs.length > 0) setSelectedSlugs(validSlugs);
  }, [maxSelections, searchParams]);
  const handleSelect = (index: number, slug: string) => {
    const newSelections = [...selectedSlugs];
    newSelections[index] = slug;
    setSelectedSlugs(newSelections);
  };
  const handleClear = (index: number) => {
    const newSelections = [...selectedSlugs];
    newSelections.splice(index, 1);
    setSelectedSlugs(newSelections);
  };
  const clearAll = () => {
    setSelectedSlugs([]);
  };
  const getProduct = (slug: string) => allProducts.find(p => p.slug === slug);
  return <div className="pt-24 pb-16 bg-[#f6f9f4] dark:bg-[#222222] min-h-screen" data-id="element-2139" data-mp="3jy56esdn8ki3">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2140" data-mp="1n2qxod38wyx0">
        <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4" data-id="element-2141" data-mp="1i45xgdnzuf8k">
          <div data-id="element-2142" data-mp="3cke24lzyrnz6">
            <Link to="/#materials" className="inline-flex items-center text-brand-muted hover:text-brand-orange transition-colors mb-4 font-bold text-sm" data-id="element-2143" data-mp="3cjmfi0och0l1">
              <ArrowLeft className="w-4 h-4 mr-1" data-id="element-2144" data-mp="32mhvo7hz1h7e" />
              Back to Materials Hub
            </Link>
            <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-brand-charcoal dark:text-white tracking-tight" data-id="element-2145" data-mp="37lb7zwliv25z">
              Compare Materials
            </h1>
            <p className="text-brand-muted dark:text-stone-300 mt-2 font-medium text-lg" data-id="element-2146" data-mp="3v9aycvex8qk1">
              Select up to {maxSelections} products to compare specs and
              details.
            </p>
          </div>
          {selectedSlugs.length > 0 && <button onClick={clearAll} className="text-sm font-bold text-brand-muted hover:text-brand-orange transition-colors self-start md:self-auto" data-id="element-2147" data-mp="3qb9ck2q42d4v">
              Clear All
            </button>}
        </div>

        {/* Selectors */}
        <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-3" data-id="element-2148" data-mp="2ey6zvir4u7pz">
          {Array.from({
          length: maxSelections
        }).map((_, i) => {
          const selectedSlug = selectedSlugs[i];
          const selectedProduct = selectedSlug ? getProduct(selectedSlug) : null;
          return <div key={i} className="bg-white dark:bg-[#2a2a2a] rounded-xl border border-stone-200 dark:border-white/10 p-4 shadow-sm relative" data-id="element-2149" data-mp="15zfb2kk3ejrs" data-mpi={i}>
                {selectedProduct ? <div className="flex items-center justify-between" data-id="element-2150" data-mp="e7zqsx60d94q">
                    <div className="flex items-center gap-3 overflow-hidden" data-id="element-2151" data-mp="99obi6wy26f4">
                      <img src={selectedProduct.images?.[0] || selectedProduct.image} alt={selectedProduct.name} className="w-12 h-12 rounded-lg object-cover shrink-0" data-id="element-2152" data-mp="97lhlbso7t54" />
                      <div className="truncate" data-id="element-2153" data-mp="i9kze6bif194">
                        <p className="text-xs font-bold text-brand-muted uppercase tracking-wider truncate" data-id="element-2154" data-mp="3qpxwnyjr8v2m">
                          {selectedProduct.categoryName}
                        </p>
                        <p className="text-sm font-extrabold text-brand-charcoal dark:text-white truncate" data-id="element-2155" data-mp="38fskrl4yx13">
                          {selectedProduct.name}
                        </p>
                      </div>
                    </div>
                    <button onClick={() => handleClear(i)} className="p-1.5 text-brand-muted hover:text-brand-orange transition-colors shrink-0" data-id="element-2156" data-mp="d5kf4siw8uif">
                      <X className="w-4 h-4" data-id="element-2157" data-mp="hz54xxac2i6j" />
                    </button>
                  </div> : <div className="relative" data-id="element-2158" data-mp="mw5ckcmtzszr">
                    <select className="w-full appearance-none bg-stone-50 dark:bg-[#2a2a2a] border border-stone-200 dark:border-white/10 rounded-lg py-3 px-4 text-sm font-bold text-brand-charcoal dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-orange" value="" onChange={e => handleSelect(i, e.target.value)} data-id="element-2159" data-mp="qxh3qmlcsma2">
                      <option value="" disabled className="bg-white dark:bg-[#2a2a2a] text-brand-charcoal dark:text-white" data-id="element-2160" data-mp="2xpro3ggy5a3x">
                        Select a product...
                      </option>
                      {categories.map(cat => <optgroup key={cat.name} label={cat.name} className="bg-white dark:bg-[#2a2a2a] text-brand-charcoal dark:text-white font-bold" data-id="element-2161" data-mp="2v5cuegb42jur">
                          {cat.products.map(p => <option key={p.slug} value={p.slug} disabled={selectedSlugs.includes(p.slug)} className="bg-white dark:bg-[#2a2a2a] text-brand-charcoal dark:text-white disabled:text-stone-400 dark:disabled:text-stone-500" data-id="element-2162" data-mp="2lf0cywylf6su">
                              {p.name}
                            </option>)}
                        </optgroup>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted pointer-events-none" data-id="element-2163" data-mp="3446y0qmv0aht" />
                  </div>}
              </div>;
        })}
        </div>

        {/* Comparison Table / Cards */}
        {selectedSlugs.length === 0 ? <div className="bg-white dark:bg-[#2a2a2a] rounded-2xl border border-stone-200 dark:border-white/10 p-12 text-center shadow-sm" data-id="element-2164" data-mp="1sn6xx8u9srd8">
            <div className="w-16 h-16 bg-stone-100 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2165" data-mp="fqrllsnvacx6">
              <Scale className="w-8 h-8 text-brand-muted" data-id="element-2166" data-mp="js3298orjnt8" />
            </div>
            <h3 className="text-xl font-extrabold text-brand-charcoal dark:text-white mb-2" data-id="element-2167" data-mp="5rsa82bpw36h">
              No products selected
            </h3>
            <p className="text-brand-muted dark:text-stone-400 font-medium" data-id="element-2168" data-mp="au120vcusz70">
              Use the dropdowns above to select materials to compare.
            </p>
          </div> : <div className="bg-white dark:bg-[#2a2a2a] rounded-2xl border border-stone-200 dark:border-white/10 shadow-sm overflow-hidden" data-id="element-2169" data-mp="vblp70lvmsv">
            <div className="overflow-x-auto" data-id="element-2170" data-mp="3o6ugextvs743">
              <table className="min-w-[760px] w-full text-left border-collapse" data-id="element-2171" data-mp="3o7w1lv4m29zj">
                <thead data-id="element-2172" data-mp="399afq340oy3q">
                  <tr className="border-b border-stone-200 dark:border-white/10 bg-stone-50 dark:bg-white/5" data-id="element-2173" data-mp="39av9vrmf6f12">
                    <th className="p-4 font-bold text-brand-muted w-1/4" data-id="element-2174" data-mp="3e855jp0ejmxc">
                      Feature
                    </th>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <th key={i} className="w-1/4 border-l border-stone-200 p-4 font-extrabold text-brand-charcoal dark:border-white/10 dark:text-white" data-id="element-2175" data-mp="2zleuhvwvmfyk" data-mpi={i}>
                          {product ? product.name : '-'}
                        </th>;
                })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200 dark:divide-white/10" data-id="element-2176" data-mp="3nbshh80ltoma">
                  {/* Price */}
                  <tr data-id="element-2177" data-mp="3idqx8jrfzj98">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300 flex items-center gap-2" data-id="element-2178" data-mp="3id7ontnom77v">
                      Price / Yard
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 font-extrabold text-brand-charcoal dark:text-white text-lg" data-id="element-2179" data-mp="2hzgypjxze444" data-mpi={i}>
                          {product ? `$${product.pricePerYard}` : '-'}
                        </td>;
                })}
                  </tr>

                  {/* Size */}
                  <tr data-id="element-2180" data-mp="1z6mt3gm8zock">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2181" data-mp="1z63di6k2lc1p">
                      <div className="flex items-center gap-2" data-id="element-2182" data-mp="245po088ike6t">
                        <Ruler className="w-4 h-4 text-brand-muted" data-id="element-2183" data-mp="2444safagqp7d" />
                        Size
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-sm font-medium text-brand-muted dark:text-stone-300" data-id="element-2184" data-mp="3tpvdg0wi2btk" data-mpi={i}>
                          {product?.specs?.size || '-'}
                        </td>;
                })}
                  </tr>

                  {/* Weight */}
                  <tr data-id="element-2185" data-mp="3ft9jh0zr974f">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2186" data-mp="3ark9loo9qzhx">
                      <div className="flex items-center gap-2" data-id="element-2187" data-mp="361c7hs0xab4n">
                        <Scale className="w-4 h-4 text-brand-muted" data-id="element-2188" data-mp="35zsr81f9o02v" />
                        Weight / Yard
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-sm font-medium text-brand-muted dark:text-stone-300" data-id="element-2189" data-mp="2n2ric5ildftn" data-mpi={i}>
                          {product?.specs?.weightPerYard || '-'}
                        </td>;
                })}
                  </tr>

                  {/* Compaction */}
                  <tr data-id="element-2190" data-mp="2zago4pa04l0w">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2191" data-mp="2v7aj1kymkzw0">
                      <div className="flex items-center gap-2" data-id="element-2192" data-mp="27h6ijmgqhg98">
                        <Layers className="w-4 h-4 text-brand-muted" data-id="element-2193" data-mp="27hpzazundylo" />
                        Compaction
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-sm font-medium text-brand-muted dark:text-stone-300" data-id="element-2194" data-mp="3rvqe1wki81gi" data-mpi={i}>
                          {product?.specs?.compaction || '-'}
                        </td>;
                })}
                  </tr>

                  {/* Coverage */}
                  <tr data-id="element-2195" data-mp="39bwt15qve7y">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2196" data-mp="3lhb2yj2uuv11">
                      <div className="flex items-center gap-2" data-id="element-2197" data-mp="3qg4ieh1pcw3u">
                        <Maximize className="w-4 h-4 text-brand-muted" data-id="element-2198" data-mp="3qge966ukn7al" />
                        Coverage
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-sm font-medium text-brand-muted dark:text-stone-300" data-id="element-2199" data-mp="d81dwrptgun5" data-mpi={i}>
                          {product?.specs?.coverage || '-'}
                        </td>;
                })}
                  </tr>

                  {/* Best For */}
                  <tr data-id="element-2200" data-mp="1fjt22ld4e0xj">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2201" data-mp="1amsug60mgq4b">
                      <div className="flex items-center gap-2" data-id="element-2202" data-mp="15nppsmok0lwv">
                        <Check className="w-4 h-4 text-brand-muted" data-id="element-2203" data-mp="15m4vmy65j4zj" />
                        Best For
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10" data-id="element-2204" data-mp="mxzwk40fw872" data-mpi={i}>
                          {product?.bestFor ? <div className="flex flex-wrap gap-1.5" data-id="element-2205" data-mp="hzyf1ninwgxp">
                              {product.bestFor.map(tag => <span key={tag} className="inline-block px-2 py-1 bg-stone-100 dark:bg-white/10 text-brand-charcoal dark:text-stone-300 text-xs font-bold rounded-md" data-id="element-2206" data-mp="dvqn7hgwqkv9">
                                  {tag}
                                </span>)}
                            </div> : '-'}
                        </td>;
                })}
                  </tr>

                  {/* Pro Tip */}
                  <tr data-id="element-2207" data-mp="2tm9yleibivuh">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2208" data-mp="2tjnpvbjoyaao">
                      <div className="flex items-center gap-2" data-id="element-2209" data-mp="2yi7hnskyugay">
                        <Lightbulb className="w-4 h-4 text-brand-orange" data-id="element-2210" data-mp="21vm3ok78mmp9" />
                        Pro Tip
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-sm font-medium text-brand-muted dark:text-stone-400 italic" data-id="element-2211" data-mp="1i5abqyeqp97u" data-mpi={i}>
                          {product?.proTip ? `"${product.proTip}"` : '-'}
                        </td>;
                })}
                  </tr>

                  {/* View Details Link */}
                  <tr data-id="element-2212" data-mp="3kr5x9vgf9975">
                    <td className="p-4" data-id="element-2213" data-mp="2fj4lycaa68u"></td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-center" data-id="element-2214" data-mp="cf0581cohtxy" data-mpi={i}>
                          {product && <Link to={`/shop/${product.slug}`} className="inline-block w-full py-2 bg-stone-100 dark:bg-white/10 hover:bg-stone-200 dark:hover:bg-white/20 text-brand-charcoal dark:text-white text-sm font-bold rounded-lg transition-colors" data-id="element-2215" data-mp="ledi7400h0nf">
                              View Details
                            </Link>}
                        </td>;
                })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>}
      </div>
    </div>;
}